import { useState } from 'react';
import { clsx } from '../../../ds/clsx';
import { Banner, Skeleton } from '../../../ds/components';
import { useSlots, type DialerCurrent, type EventTypeRef, type Glow } from '../api';
import { EventTypeCombobox } from '../EventTypeCombobox';
import { dayKeyPlus, slotDayLabel, slotTime, todayKey } from '../format';
import { Eyebrow, FactRow } from '../ui';
import styles from './session.module.css';

const SLOT_DAYS = 3;
const SLOTS_PER_DAY = 4;
const SEARCH_DAYS = 21;

/**
 * „Termin buchen · vorausgefüllt" (Terminierung 08/08b): the event type of the contact's list („aus Liste …",
 * „ändern" opens the search) or, in several lists with different types, the empty field with the warning
 * pill and the open search; closer and participant; the free slots of the next three days with times, one
 * click books (`POST /dialer/sessions/:id/book`).
 */
export function BookingCard({
  current,
  chosen,
  onChoose,
  booked,
  booking,
  error,
  canBook,
  onBook,
}: {
  current: DialerCurrent;
  chosen: EventTypeRef | null;
  onChoose: (eventType: EventTypeRef) => void;
  booked: Glow | null;
  booking: string | null;
  error: string | null;
  /** A call was placed in this session (the server books only then). */
  canBook: boolean;
  onBook: (eventType: EventTypeRef, startsAt: string, advisorId: string) => void;
}) {
  const choice = current.eventTypeChoice;
  const effective = chosen ?? current.eventType;
  const [searching, setSearching] = useState(choice !== null && chosen === null);
  const today = todayKey();
  const slots = useSlots(effective?.id ?? null, today, dayKeyPlus(today, SEARCH_DAYS));
  const days = (slots.data?.days ?? [])
    .filter((day) => day.slots.length > 0)
    .slice(0, SLOT_DAYS)
    .map((day) => ({ ...day, slots: day.slots.slice(0, SLOTS_PER_DAY) }));
  const fromList = chosen === null && choice === null && current.eventTypeListName !== null;

  return (
    <section className={styles.card} aria-labelledby="dialer-booking-title">
      <div className={styles.cardHead}>
        <Eyebrow>
          <span id="dialer-booking-title">Termin buchen</span>
        </Eyebrow>
        <span className={styles.cardHeadNote}>vorausgefüllt</span>
      </div>
      <div className={styles.bookingBody}>
        <div className={styles.bookingFacts}>
          {searching || effective === null ? (
            <div className={styles.eventSearch}>
              <div className={styles.eventSearchHead}>
                <span className={styles.factLabelSmall}>Ereignis</span>
                {choice !== null && chosen === null ? (
                  <span className={styles.warningPill}>in {choice.lists.length} Listen · bitte wählen</span>
                ) : null}
              </div>
              <EventTypeCombobox
                contactId={current.contact.id}
                value={chosen}
                onChange={(value) => {
                  onChoose(value);
                  setSearching(false);
                }}
                open={searching}
                onOpenChange={setSearching}
                label="Ereignis"
              />
            </div>
          ) : (
            <div className={styles.eventBox}>
              <FactRow
                label="Ereignis"
                value={
                  <span className={styles.eventName}>
                    {effective.name} · {effective.durationMinutes} Min.
                  </span>
                }
              />
              <div className={styles.eventSource}>
                <span className={styles.eventSourceText}>
                  {fromList ? `aus Liste „${current.eventTypeListName ?? ''}“` : 'selbst gewählt'}
                </span>
                {booked === null ? (
                  <button type="button" className={styles.linkButton} onClick={() => setSearching(true)}>
                    ändern
                  </button>
                ) : null}
              </div>
            </div>
          )}
          <FactRow
            label="Closer"
            value={chosen === null ? (current.closerLine ?? '—') : 'aus dem Ereignis'}
          />
          <FactRow label="Teilnehmer" value={current.contact.name} />
        </div>
        {error ? (
          <div className={styles.bookingError}>
            <Banner tone="danger">{error}</Banner>
          </div>
        ) : null}
        {booked !== null ? (
          <div className={styles.bookedBox} role="status">
            <Eyebrow tone="accent">Termin vereinbart</Eyebrow>
            <div className={styles.bookedWhen}>
              {booked.dateLabel} · {booked.timeLabel}
            </div>
            <div className={styles.bookedLine}>{booked.calendarLine}</div>
          </div>
        ) : effective === null ? null : (
          <>
            <Eyebrow className={styles.slotsEyebrow}>Freie Slots</Eyebrow>
            <div className={styles.slotDays}>
              {slots.isPending ? (
                <Skeleton width="100%" height={72} />
              ) : slots.isError ? (
                <p className={styles.slotsNote}>Die freien Zeiten konnten nicht geladen werden.</p>
              ) : days.length === 0 ? (
                <p className={styles.slotsNote}>Keine freien Zeiten in den nächsten drei Wochen.</p>
              ) : (
                days.map((day) => (
                  <div key={day.date}>
                    <div className={styles.slotDay}>{slotDayLabel(day.date, today)}</div>
                    <div className={styles.slotTimes}>
                      {day.slots.map((slot) => (
                        <button
                          key={slot.startsAt}
                          type="button"
                          className={clsx(styles.slot, booking === slot.startsAt && styles.slotBusy)}
                          disabled={!canBook || booking !== null}
                          aria-busy={booking === slot.startsAt || undefined}
                          aria-label={`${slotDayLabel(day.date, today)}, ${slotTime(slot.startsAt)} buchen`}
                          onClick={() => onBook(effective, slot.startsAt, slot.advisorId)}
                        >
                          {slotTime(slot.startsAt)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
            {!canBook && days.length > 0 ? (
              <p className={styles.slotsNote}>Gebucht wird im Gespräch — rufen Sie zuerst an.</p>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}
